import { defer, type LoaderFunctionArgs } from 'react-router-dom';
import {
  fetchJSON,
  fetchDemo,
  fetchText,
  getDocumentDirectory,
  vchartGuideDefaultRoute,
  vtableGuideDefaultRoute,
  vgraphGuideDefaultRoute,
  vgrammarGuideDefaultRoute,
  vrenderGuideDefaultRoute,
  vchartOptionDefaultRoute,
  vtableOptionDefaultRoute,
  vgrammarOptionDefaultRoute,
  vchartAPIDefaultRoute,
  vtableAPIDefaultRoute,
  vgrammarAPIDefaultRoute,
  visactorBlogDefaultRoute,
  vchartFAQDefaultRoute,
  vtableFAQDefaultRoute,
  vchartChangelogDefaultRoute,
  vgrammarChangelogDefaultRoute,
  vgraphChangelogDefaultRoute,
  vmindGuideDefaultRoute,
  vmindAPIDefaultRoute,
  vmindScenarioDefaultRoute,
  fetchExamples,
  LanguageEnum,
  normalizeLoaderPath,
  vchartContributingDefaultRoute,
  vtableContributingDefaultRoute,
} from './utils';
import { parseDescription } from './components/option-document/description';
import { parseMenuOutline } from '@/components/markdown-document/outline';

export async function apiLoader({ request, params }: LoaderFunctionArgs) {
  const getURL = () => {
    const { pathname } = new URL(request.url);
    const defaultParamsMap = {
      vchart: vchartAPIDefaultRoute,
      vtable: vtableAPIDefaultRoute,
      vgrammar: vgrammarAPIDefaultRoute,
      vmind: vmindAPIDefaultRoute,
    } as const;
    const root = pathname.match(
      /^\/([^/]+)/
    )![1] as keyof typeof defaultParamsMap;
    return {
      root: root,
      defaultParams: defaultParamsMap[root],
    };
  };
  const { root, defaultParams } = getURL();

  const fetchMenu = () =>
    fetchJSON(`${getDocumentDirectory(root)}/api/menu.json`).catch(() => null);
  const fetchContent = async () => {
    const path = normalizeLoaderPath(params['*'] ?? defaultParams);
    const baseURL = `${getDocumentDirectory(root)}/api`;
    // 要是能把语言放到 querystring 获取能够感知到的地方
    // 就不需要同时抓两个了
    const [zhData, enData] = await Promise.all([
      fetchText(`${baseURL}/zh/${path}.md`).catch(() => ''),
      fetchText(`${baseURL}/en/${path}.md`).catch(() => ''),
    ]);
    return { zh: zhData, en: enData };
  };
  const [menu, content] = await Promise.all([fetchMenu(), fetchContent()]);
  return { menu, content };
}
/**
 * Loads content for the contributing guide.
 *
 * This function is responsible for fetching the content and menu for the contributing guide.
 * It uses the request and params to determine the language and the specific page to load.
 *
 * @param {LoaderFunctionArgs} { request, params } - The loader function arguments containing the request and params.
 * @returns {Promise<{ content: { zh: string, en: string }, menu: any }>} - A promise that resolves to an object containing the content and menu.
 */

export async function contributingLoader({
  request,
  params,
}: LoaderFunctionArgs) {
  const getURL = () => {
    const { pathname } = new URL(request.url);
    const defaultParamsMap = {
      vchart: vchartContributingDefaultRoute,
      vtable: vtableContributingDefaultRoute,
      vgrammar: vchartContributingDefaultRoute,
      vrender: vchartContributingDefaultRoute,
      vmind: vchartContributingDefaultRoute,
      vstory: vchartContributingDefaultRoute,
    } as const;
    const root = pathname.match(
      /^\/([^/]+)/
    )![1] as keyof typeof defaultParamsMap;
    return {
      root: root,
      defaultParams: defaultParamsMap[root],
    };
  };
  const { root, defaultParams } = getURL();

  const fetchMenu = () =>
    fetchJSON(`${getDocumentDirectory(root)}/contributing/menu.json`).catch(
      () => null
    );
  const fetchContent = async () => {
    const path = normalizeLoaderPath(params['*'] ?? defaultParams);
    const baseURL = `${getDocumentDirectory(root)}/contributing`;
    // 要是能把语言放到 querystring 获取能够感知到的地方
    // 就不需要同时抓两个了
    const [zhData, enData] = await Promise.all([
      fetchText(`${baseURL}/zh/${path}.md`).catch(() => ''),
      fetchText(`${baseURL}/en/${path}.md`).catch(() => ''),
    ]);
    return { zh: zhData, en: enData };
  };
  const [content, menu] = await Promise.all([fetchContent(), fetchMenu()]);

  return { content, menu };
}

export async function guideLoader({ request, params }: LoaderFunctionArgs) {
  const getURL = () => {
    const { pathname } = new URL(request.url);
    const defaultParamsMap = {
      vchart: vchartGuideDefaultRoute,
      vtable: vtableGuideDefaultRoute,
      vgraph: vgraphGuideDefaultRoute,
      vgrammar: vgrammarGuideDefaultRoute,
      vrender: vrenderGuideDefaultRoute,
      vmind: vmindGuideDefaultRoute,
    } as const;
    const root = pathname.match(
      /^\/([^/]+)/
    )![1] as keyof typeof defaultParamsMap;
    return {
      root: root,
      defaultParams: defaultParamsMap[root],
    };
  };
  const { root, defaultParams } = getURL();

  const fetchMenu = () =>
    fetchJSON(`${getDocumentDirectory(root)}/guide/menu.json`).catch(
      () => null
    );
  const fetchContent = async () => {
    const path = normalizeLoaderPath(params['*'] ?? defaultParams);
    const baseURL = `${getDocumentDirectory(root)}/guide`;
    // 要是能把语言放到 querystring 获取能够感知到的地方
    // 就不需要同时抓两个了
    const [zhData, enData] = await Promise.all([
      fetchText(`${baseURL}/zh/${path}.md`).catch(() => ''),
      fetchText(`${baseURL}/en/${path}.md`).catch(() => ''),
    ]);
    return { zh: zhData, en: enData };
  };
  const [content, menu] = await Promise.all([fetchContent(), fetchMenu()]);

  return { content, menu };
}

export async function faqLoader({ request, params }: LoaderFunctionArgs) {
  const getURL = () => {
    const { pathname } = new URL(request.url);
    const defaultParamsMap = {
      vchart: vchartFAQDefaultRoute,
      vtable: vtableFAQDefaultRoute,
    } as const;
    const root = pathname.match(
      /^\/([^/]+)/
    )![1] as keyof typeof defaultParamsMap;
    return {
      root: root,
      defaultParams: defaultParamsMap[root],
    };
  };
  const { root, defaultParams } = getURL();

  const fetchMenu = () =>
    fetchJSON(`${getDocumentDirectory(root)}/faq/menu.json`).catch(() => null);
  const fetchContent = async () => {
    const path = normalizeLoaderPath(params['*'] ?? defaultParams);
    const baseURL = `${getDocumentDirectory(root)}/faq`;
    // 要是能把语言放到 querystring 获取能够感知到的地方
    // 就不需要同时抓两个了
    const [zhData, enData] = await Promise.all([
      fetchText(`${baseURL}/zh/${path}.md`),
      fetchText(`${baseURL}/en/${path}.md`),
    ]);
    return { zh: zhData, en: enData };
  };
  const [content, menu] = await Promise.all([fetchContent(), fetchMenu()]);
  return { menu, content };
}

export async function exampleLoader({ request, params }: LoaderFunctionArgs) {
  try {
    const { pathname } = new URL(request.url);
    const splitPathname = pathname.split('/');
    const root = splitPathname[1];
    const path =
      root === 'vtable' || root === 'vgraph'
        ? splitPathname[2].replace('example', 'demo')
        : splitPathname[2].replace('example', 'examples');
    const menu = await fetchExamples(
      `${getDocumentDirectory(root)}/${path}/menu.json`
    );
    return menu ?? [];
  } catch {
    return [];
  }
}

export async function demoLoader({ request, params }: LoaderFunctionArgs) {
  const { pathname, search } = new URL(request.url);
  const splitPathname = pathname.split('/');
  const root = splitPathname[1];
  const path =
    root === 'vtable' || root === 'vgraph'
      ? splitPathname[2]
      : splitPathname[2].replace('demo', 'examples');

  const [en, zh] = await Promise.all([
    fetchDemo(
      `${getDocumentDirectory(root)}/${path}/en/${params['*']}.md`,
      `${getDocumentDirectory(root)}/${path}/menu.json`,
      params['*']!,
      search
    ),
    fetchDemo(
      `${getDocumentDirectory(root)}/${path}/zh/${params['*']}.md`,
      `${getDocumentDirectory(root)}/${path}/menu.json`,
      params['*']!,
      search
    ),
  ]);
  return { en, zh };
}

export async function optionLoader({
  request,
  params,
  language,
}: Pick<LoaderFunctionArgs, 'request' | 'params'> & {
  language?: LanguageEnum;
}) {
  const getURL = () => {
    const { pathname } = new URL(request.url);
    const defaultParamsMap = {
      vchart: vchartOptionDefaultRoute,
      vtable: vtableOptionDefaultRoute,
      vgrammar: vgrammarOptionDefaultRoute,
    } as const;
    const root = pathname.match(
      /^\/([^/]+)/
    )![1] as keyof typeof defaultParamsMap;
    return {
      root: root,
      defaultParams: defaultParamsMap[root],
    };
  };
  const { root, defaultParams } = getURL();

  const getOutline = async (lang: 'zh' | 'en') => {
    try {
      const json = await fetchJSON(
        `${getDocumentDirectory(root)}/option/${lang}/outline.json`
      );
      return json;
    } catch (e) {
      console.log(e);
    }
    return null;
  };
  const getDescription = async (lang: 'zh' | 'en') => {
    try {
      const optionPath = params['*'] ?? defaultParams;
      const basePath = optionPath.split('.')[0];
      const json = await fetchJSON(
        !basePath || basePath === ''
          ? `${getDocumentDirectory(root)}/option/${lang}/option.json`
          : `${getDocumentDirectory(
              root
            )}/option/${lang}/option.${basePath}.json`
      );
      return parseDescription(json, basePath);
    } catch (e) {
      console.log(e);
    }
    return '';
  };

  if (language) {
    if (root === 'vchart') {
      const description = await getDescription(language);
      return defer({
        [language]: {
          outline: getOutline(language),
          description: description,
        },
      });
    } else {
      const [outline, description] = await Promise.all([
        getOutline(language),
        getDescription(language),
      ]);
      return {
        [language]: {
          outline: outline,
          description: description,
        },
      };
    }
  }

  if (root === 'vchart') {
    const [descriptionZh, descriptionEn] = await Promise.all([
      getDescription('zh'),
      getDescription('en'),
    ]);
    return defer({
      zh: {
        outline: getOutline('zh'),
        description: descriptionZh,
      },
      en: {
        outline: getOutline('en'),
        description: descriptionEn,
      },
    });
  } else {
    const [outlineZh, outlineEn, descriptionZh, descriptionEn] =
      await Promise.all([
        getOutline('zh'),
        getOutline('en'),
        getDescription('zh'),
        getDescription('en'),
      ]);

    return {
      zh: {
        outline: outlineZh,
        description: descriptionZh,
      },
      en: {
        outline: outlineEn,
        description: descriptionEn,
      },
    };
  }
}

export async function vTableApiLoader({ params }: LoaderFunctionArgs) {
  const optionPath = params['*'] ?? vtableAPIDefaultRoute;
  const basePath = optionPath.split('.')[0];

  const [outlineZh, outlineEn, descriptionZh, descriptionEn] =
    await Promise.all([
      fetchJSON(`${getDocumentDirectory('vtable')}/api/zh/outline.json`),
      fetchJSON(`${getDocumentDirectory('vtable')}/api/en/outline.json`),
      fetchJSON(
        !basePath || basePath === ''
          ? `${getDocumentDirectory('vtable')}/api/zh/api.json`
          : `${getDocumentDirectory('vtable')}/api/zh/api.${basePath}.json`
      ),
      fetchJSON(
        !basePath || basePath === ''
          ? `${getDocumentDirectory('vtable')}/api/en/api.json`
          : `${getDocumentDirectory('vtable')}/api/en/api.${basePath}.json`
      ),
    ]);

  return {
    zh: {
      outline: outlineZh,
      descriptioin: parseDescription(descriptionZh, basePath),
    },
    en: {
      outline: outlineEn,
      descriptioin: parseDescription(descriptionEn, basePath),
    },
  };
}

export async function blogLoader({ params }: LoaderFunctionArgs) {
  const getOutline = async (language: 'zh' | 'en') => {
    try {
      const menu = await fetchJSON('/documents/visactor/blog/menu.json');
      return (menu?.children ?? []).map((menuItem: any) =>
        parseMenuOutline(menuItem, language as any)
      );
    } catch (e) {
      console.log(e);
    }
    return null;
  };
  const getContent = async (language: 'zh' | 'en') => {
    try {
      const fullPath = normalizeLoaderPath(
        params['*'] ?? visactorBlogDefaultRoute
      );
      const document = await fetchText(
        `/documents/visactor/blog/${language}/${fullPath}.md`
      );
      return document ?? '';
    } catch (e) {
      console.log(e);
    }
    return '';
  };

  const [outlineZh, outlineEn, contentZh, contentEn] = await Promise.all([
    getOutline('zh'),
    getOutline('en'),
    getContent('zh'),
    getContent('en'),
  ]);
  return {
    zh: {
      outline: outlineZh,
      content: contentZh,
    },
    en: {
      outline: outlineEn,
      content: contentEn,
    },
  };
}

export async function changelogLoader({ request, params }: LoaderFunctionArgs) {
  const getURL = () => {
    const { pathname } = new URL(request.url);
    const defaultParamsMap = {
      vchart: vchartChangelogDefaultRoute,
      vgrammar: vgrammarChangelogDefaultRoute,
      vgraph: vgraphChangelogDefaultRoute,
    } as const;
    const root = pathname.match(
      /^\/([^/]+)/
    )![1] as keyof typeof defaultParamsMap;
    return {
      root: root,
      defaultParams: defaultParamsMap[root],
    };
  };
  const { root, defaultParams } = getURL();

  const fetchMenu = () =>
    fetchJSON(`${getDocumentDirectory(root)}/changelog/menu.json`).catch(
      () => null
    );
  const fetchContent = async () => {
    const path = normalizeLoaderPath(params['*'] ?? defaultParams);
    const baseURL = `${getDocumentDirectory(root)}/changelog`;
    const [zhData, enData] = await Promise.all([
      fetchText(`${baseURL}/zh/${path}.md`).catch(() => ''),
      fetchText(`${baseURL}/en/${path}.md`).catch(() => ''),
    ]);
    return { zh: zhData, en: enData };
  };
  const [menu, content] = await Promise.all([fetchMenu(), fetchContent()]);
  return { menu, content };
}

export async function scenarioLoader({ request, params }: LoaderFunctionArgs) {
  const getURL = () => {
    const { pathname } = new URL(request.url);
    const defaultParamsMap = {
      vmind: vmindScenarioDefaultRoute,
    } as const;
    const root = pathname.match(
      /^\/([^/]+)/
    )![1] as keyof typeof defaultParamsMap;
    return {
      root: root,
      defaultParams: defaultParamsMap[root],
    };
  };
  const { root, defaultParams } = getURL();

  const fetchMenu = () =>
    fetchJSON(`${getDocumentDirectory(root)}/scenario/menu.json`).catch(
      () => null
    );
  const fetchContent = async () => {
    const path = params['*'] ?? defaultParams;
    const baseURL = `${getDocumentDirectory(root)}/scenario`;
    // 要是能把语言放到 querystring 获取能够感知到的地方
    // 就不需要同时抓两个了
    const [zhData, enData] = await Promise.all([
      fetchText(`${baseURL}/zh/${path}.md`).catch(() => ''),
      fetchText(`${baseURL}/en/${path}.md`).catch(() => ''),
    ]);
    return { zh: zhData, en: enData };
  };
  const [menu, content] = await Promise.all([fetchMenu(), fetchContent()]);
  return { menu, content };
}

export async function entryLoader({ request, params }: LoaderFunctionArgs) {
  const { pathname } = new URL(request.url);
  const splitPathname = pathname.split('/');
  const root = splitPathname[1];

  // guide
  const fetchGuideMenu = () =>
    fetchJSON(`${getDocumentDirectory(root)}/guide/menu.json`).catch(
      () => null
    );

  // example
  const examplePath =
    root === 'vtable'
      ? splitPathname[2].replace('entry', 'demo')
      : splitPathname[2].replace('entry', 'examples');
  const fetchExampleMenu = () =>
    fetchExamples(`${getDocumentDirectory(root)}/${examplePath}/menu.json`);

  // changelog
  const fetchChangelogMenu = () =>
    fetchJSON(`${getDocumentDirectory(root)}/changelog/menu.json`).catch(
      () => null
    );

  // faq
  const fetchFAQMenu = () =>
    fetchJSON(`${getDocumentDirectory(root)}/faq/menu.json`).catch(() => null);

  // api
  const fetchAPIMenu = () =>
    fetchJSON(`${getDocumentDirectory(root)}/api/menu.json`).catch(() => null);

  // contribute
  const fetchContributeMenu = () =>
    fetchJSON(`${getDocumentDirectory(root)}/contributing/menu.json`).catch(
      () => null
    );

  // scenario
  const fetchScenarioMenu = () =>
    fetchJSON(`${getDocumentDirectory(root)}/scenario/menu.json`).catch(
      () => null
    );

  const [guide, example, changelog, faq, api, contributing, scenario] =
    await Promise.all([
      fetchGuideMenu(),
      fetchExampleMenu(),
      fetchChangelogMenu(),
      fetchFAQMenu(),
      fetchAPIMenu(),
      fetchContributeMenu(),
      fetchScenarioMenu(),
    ]);

  return [
    {
      type: 'markdown',
      key: 'guide',
      data: guide,
    },
    {
      type: 'demo',
      key: 'demo',
      data: example,
    },
    {
      type: 'markdown',
      key: 'changelog',
      data: changelog,
    },
    {
      type: 'markdown',
      key: 'faq',
      data: faq,
    },
    {
      type: 'markdown',
      key: 'api',
      data: api,
    },
    {
      type: 'markdown',
      key: 'contributing',
      data: contributing,
    },
    {
      type: 'markdown',
      key: 'scenario',
      data: scenario,
    },
  ];
}
